import { alias, bool, equal, or, sort } from '@ember/object/computed';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo, hasMany } from 'ember-data/relationships';
import notEqual from 'cio/utils/not-equal';
import { modelApiAction } from 'cio/utils/model-api-actions';
import { SYNC_STATUSES } from 'cio/utils/ad-audiences';
import AudienceModel from 'cio/models/audience';
import TagModel from 'cio/models/tag';
import UserOtherAccountModel from 'cio/models/user-other-account';
import DS from 'ember-data';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { SerializedConditions } from 'cio/types/conditions/condition';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import { FormType, isCustomForm } from 'cio/utils/forms';
import CustomFormsService from 'cio/services/custom-forms';
import { SegmentUsage } from 'cio/utils/segment-usage';
import ObjectTypeModel from 'cio/models/object-type';
import isDestroyed from 'cio/utils/is-destroyed';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CustomForm from 'cio/models/custom-form';

export const SEGMENT_TYPES = {
  default: 'all',
  manual: 'static',
  dynamic: 'dynamic',
};

type Error = {
  key: string;
  message: string;
};

export type SegmentType = 'static' | 'dynamic';

export default class SegmentModel extends Model {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service customForms!: CustomFormsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service store!: DS.Store;

  @attr('string') type!: SegmentType;
  @attr('string') name!: string;
  @attr() conditions!: SerializedConditions;
  @attr('string') state!: string;
  @attr('number') progress!: number;
  @attr('number') updatedAt!: number;
  @attr('number') createdAt!: number;
  @attr('string') description!: string;
  @attr('boolean') archived!: boolean;
  @attr('string') autoArchivedAt?: number;
  @attr('boolean') inUseActive!: boolean;
  @attr('boolean') inUseArchived!: boolean; // used by any archived campaigns
  @attr('segment/usage') usage?: SegmentUsage;
  @attr('boolean') hasRelationshipConditions?: boolean; // used to quickly detect segments with object relationships

  // Relevant to auto-created segments for Facebook Lead Ads forms:
  @attr('string') linkedFormType!: FormType;
  @attr('string') linkedFormId!: string;

  @hasMany('audience', { async: true, inverse: 'segment' }) audiences!: DS.PromiseManyArray<AudienceModel>;
  @hasMany('tag', { async: true, inverse: 'segments' }) tags!: DS.PromiseManyArray<TagModel>;
  @hasMany('object-type', { async: false, inverse: null }) objectTypes!: ObjectTypeModel[];

  @belongsTo('user-other-account', { async: false, inverse: 'createdSegments' }) createdByUser!: UserOtherAccountModel;
  @belongsTo('user-other-account', { async: false, inverse: 'updatedSegments' }) updatedByUser!: UserOtherAccountModel;

  loadedPeopleCount: number | null = null;

  tagSorting = ['name'];
  @sort('tags.content', 'tagSorting') sortedTags!: TagModel[];

  // Five states of processing
  @equal('state', 'events_queued') isEventsQueued!: boolean;
  @equal('state', 'events') isEvents!: boolean;
  @equal('state', 'build_queued') isBuildQueued!: boolean;

  // Only state that shows progress
  @notEqual('state', 'finished') isBuilding!: boolean;
  @equal('state', 'finished') isFinished!: boolean;

  // States that do not show progress
  @or('isEventsQueued', 'isEvents', 'isBuildQueued') isPreProcessing!: boolean;

  @equal('type', 'dynamic') isDynamic!: boolean;
  @equal('type', 'static') isManual!: boolean;

  @bool('linkedFormId') isLinkedToForm!: boolean;
  @computed('isLinkedToForm', 'linkedFormType', 'facebookLeadAds.forms.[]', 'customForms.forms.[]')
  private get linkedForm(): FacebookLeadAdsForm | CustomForm | null | undefined {
    if (this.linkedFormType == null) {
      return null;
    }

    if (this.linkedFormType === FormType.FacebookLeadAds) {
      if (!this.facebookLeadAds.initialized) {
        throw new Error("can't access property before initializing facebook-lead-ads service");
      }

      return this.facebookLeadAds.formsByFormID[this.linkedFormId];
    }

    // all other form types are "custom forms" internally:
    if (!this.customForms.initialized) {
      throw new Error("can't access property before initializing custom-forms service");
    }

    return this.customForms.forms.findBy('formId', this.linkedFormId);
  }

  @computed('linkedForm')
  get isLinkedToAnyNonDeletedForm() {
    return Boolean(this.linkedForm);
  }

  @computed('linkedForm')
  get isLinkedToConnectedForm() {
    if (!this.linkedForm) {
      return false;
    }

    return isCustomForm(this.linkedForm) ? this.linkedForm.isConnectedOrDraft : this.linkedForm.isConnected;
  }

  @computed('progress')
  get hasProgress() {
    return this.progress && this.progress !== 0 ? true : false;
  }

  @computed('isManual')
  get icon() {
    return this.isManual ? 'segment-manual' : 'segment-data-driven';
  }

  // "segment.usage" is not available for archived segments, but archived campaigns can still use them
  // "inUseArchived" boolean is available instead to recognize this scenario:
  @alias('inUseArchived') usedInArchivedCampaigns!: boolean;

  @computed('usage')
  get usedInActiveCampaigns() {
    return !isEmpty(this.usage?.campaigns) && this.usage?.campaigns.some((c) => !c.archived);
  }

  @or('usedInActiveCampaigns', 'usedInArchivedCampaigns') usedInAnyCampaigns!: boolean;
  @or('usedInActiveCampaigns', 'usedInUnsentNewsletters') usedInActiveCampaignsOrUnsentNewsletters!: boolean;
  @or('usedInAnyCampaigns', 'usedInUnsentNewsletters') usedInAnyCampaignsOrUnsentNewsletters!: boolean;

  @or('usedInActiveCampaigns', 'usedInUnsentNewsletters', 'usedInApiTriggeredBroadcasts')
  usedInActiveCampaignsOrUnsentNewslettersOrBroadcasts!: boolean;
  @or('usedInAnyCampaigns', 'usedInUnsentNewsletters', 'usedInApiTriggeredBroadcasts')
  usedInAnyCampaignsOrUnsentNewslettersOrBroadcasts!: boolean;
  @or('usedInAnyCampaigns', 'usedInAnyNewsletters', 'usedInApiTriggeredBroadcasts')
  usedInAnyCampaignsOrAnyNewslettersOrBroadcasts!: boolean;

  // Unsent newsletters includes anything that's not yet sent, i.e. draft or scheduled.
  @computed('usage')
  get usedInUnsentNewsletters() {
    return !isEmpty(this.usage?.draftNewsletters);
  }

  @computed('usage')
  get usedInAnyNewsletters() {
    return !isEmpty(this.usage?.newsletters);
  }

  @computed('usage')
  get usedInApiTriggeredBroadcasts() {
    return !isEmpty(this.usage?.broadcasts);
  }

  @computed('usage', 'usedInActiveCampaigns', 'usedInUnsentNewsletters')
  get usedInOnlySentNewsletters() {
    return (
      !this.usedInActiveCampaigns &&
      !this.usedInApiTriggeredBroadcasts &&
      !this.usedInUnsentNewsletters &&
      !isEmpty(this.usage?.sentNewsletters)
    );
  }

  @computed('usage')
  get usedInSqlImports() {
    return (this.usage?.sqlImports?.length ?? 0) > 0;
  }

  @computed('usage')
  get usedInDashboards() {
    return (this.usage?.dashboard?.length ?? 0) > 0;
  }

  @or(
    'usedInAnyCampaigns',
    'usedInAnyNewsletters',
    'usedInApiTriggeredBroadcasts',
    'usedInSqlImports',
    'usedInDashboards',
  )
  usedByAnything!: boolean;

  @computed('usedInActiveCampaignsOrUnsentNewslettersOrBroadcasts', 'isLinkedToConnectedForm', 'usedInSqlImports')
  get canBeArchived() {
    return (
      !this.archived &&
      !this.usedInActiveCampaignsOrUnsentNewslettersOrBroadcasts &&
      !this.isLinkedToConnectedForm &&
      !this.usedInSqlImports
    );
  }

  @computed('usedInAnyCampaignsOrUnsentNewslettersOrBroadcasts', 'isLinkedToAnyNonDeletedForm', 'usedInSqlImports')
  get canBeDeleted() {
    return (
      !this.usedInAnyCampaignsOrUnsentNewslettersOrBroadcasts &&
      !this.isLinkedToAnyNonDeletedForm &&
      !this.usedInSqlImports
    );
  }

  validate(): Error[] {
    let errors: Error[] = [];
    let { name, conditions } = this;

    if (isEmpty(name)) {
      errors.pushObject({
        key: 'name',
        message: 'Please enter a name for your segment.',
      });
    }

    // @ts-ignore: conditions not typed yet
    if (!conditions || isEmpty(conditions.and)) {
      errors.pushObject({
        key: 'conditions',
        message: 'Please enter at least one condition in your segment.',
      });
    }

    return errors;
  }

  _loadPeopleCount = modelApiAction({
    path: 'count',
    type: 'GET',
  });

  async loadPeopleCount() {
    let peopleCount = await this._loadPeopleCount({});

    if (peopleCount?.count != null) {
      this.set('loadedPeopleCount', peopleCount.count);
    }
    return peopleCount?.count;
  }

  _updateMembers = modelApiAction({
    path: 'members',
    type: 'PUT',
  });

  clear() {
    return this._updateMembers({ type: 'clear' });
  }

  addTags = modelApiAction({
    path: 'tags',
    type: 'POST',
    before(tags: TagModel[]) {
      return {
        tags: tags.map((tag) => ({ id: Number(tag.id), name: tag.name })),
      };
    },

    after(this: SegmentModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  async removeTag(tag: TagModel) {
    await this.api.post(`segments/${this.id}/tags_delete/${tag.id}`, {});

    this.set(
      'tags',
      (this.tags.content as TagModel[]).filter((t) => t !== tag),
    );
  }

  // audiences
  get audiencesWithErrors() {
    if (isDestroyed(this)) {
      // when deleting it's possible that this will be accessed on a
      // destroyed segment, so return early: http://127.0.0.1:4200/tests?testId=4c6308fe
      return [];
    }

    let audiences = (this as SegmentModel).hasMany('audiences').value()?.toArray() ?? [];

    return audiences.filter((audience) => audience.status === SYNC_STATUSES.error.id);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    segment: SegmentModel;
  }
}
